import { Type } from "../types/structs";
import { typeData as generatedTypeData, TypeInfo } from "../generated/type-metadata";
import { TYPE_EFFECTIVENESS_CHART as generatedChart, EffectivenessValue } from "../generated/type-chart";

export type { TypeInfo, EffectivenessValue };

// Real types only (excludes the Type.None single-type sentinel).
export type RealType = Exclude<Type, Type.None>;

// Per-type display metadata (kanji/colors/emoji/abbrev) is generated from
// drool/type-metadata.csv; the effectiveness chart from drool/types.csv. Both only have
// entries for real types — None (the single-type sentinel) carries no data and is never
// indexed in the UI — but are exposed as Type-indexable for caller ergonomics.
export const typeData = generatedTypeData as Record<Type, TypeInfo>;
export const TYPE_EFFECTIVENESS_CHART = generatedChart as Record<Type, Record<Type, EffectivenessValue>>;

export enum Effectiveness {
  Useless = 0,
  SuperWeak = 0.25,
  Weak = 0.5,
  Normal = 1,
  Strong = 2,
  SuperStrong = 4,
}

// All real types in enum order (excludes None), derived from the Type enum so it stays
// in sync as types are added/removed.
export const ALL_TYPES: ReadonlyArray<RealType> = Object.values(Type).filter(
  (v): v is RealType => typeof v === 'number' && v !== Type.None,
);

export interface DefensiveMatchup {
  weak4x: Type[];
  weak2x: Type[];
  resist05: Type[];
  resist025: Type[];
  immune: Type[];
}

// Incoming-damage multipliers for a (t1, t2?) defender. t2 null/undefined →
// single-type (factor 1 for the second type). Bins each attacker into the
// non-neutral bucket it falls into; ×1 (neutral) is left implicit.
export function defensiveMatchup(t1: Type, t2: Type | null | undefined): DefensiveMatchup {
  const out: DefensiveMatchup = {
    weak4x: [], weak2x: [], resist05: [], resist025: [], immune: [],
  };
  for (const attacker of ALL_TYPES) {
    const m1 = TYPE_EFFECTIVENESS_CHART[attacker][t1];
    const m2 = t2 != null ? TYPE_EFFECTIVENESS_CHART[attacker][t2] : 1;
    const m = m1 * m2;
    if (m === 0) out.immune.push(attacker);
    else if (m === 0.25) out.resist025.push(attacker);
    else if (m === 0.5) out.resist05.push(attacker);
    else if (m === 2) out.weak2x.push(attacker);
    else if (m === 4) out.weak4x.push(attacker);
  }
  return out;
}

// Effectiveness → swatch color (battle-reachable UI; literal hexes intentional). Shared by the
// move-button and expandable-move-card damage badges.
export const EFFECTIVENESS_COLORS: Record<Effectiveness, string> = {
  [Effectiveness.Useless]: '#879d87ff',
  [Effectiveness.SuperWeak]: '#cc0000',
  [Effectiveness.Weak]: '#ff6b6b',
  [Effectiveness.Normal]: '#d4f7d2',
  [Effectiveness.Strong]: '#51cf66',
  [Effectiveness.SuperStrong]: '#55aaffff',
};

export function multiplierGlyph(mult: number): string {
  switch (mult) {
    case 4: return '×4';
    case 2: return '×2';
    case 0.5: return '×½';
    case 0.25: return '×¼';
    case 0: return '×0';
    default: return `×${mult}`;
  }
}
